// One loading, error, and data state for every page, so no page invents its
// own fetch handling. `load` is a call through the typed Hono client in
// ./client.ts, so the result type comes from apps/api's real route types.
import { useCallback, useEffect, useRef, useState } from "react";

export type ApiState<T> =
  | { status: "loading"; data: null; error: null }
  | { status: "ready"; data: T; error: null }
  | { status: "error"; data: null; error: string };

/** Turn a typed client response into its JSON body, or throw a readable error. */
export async function readJson<T>(res: { ok: boolean; status: number; json(): Promise<T> }): Promise<T> {
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as unknown as { error?: string };
      detail = body?.error ? `: ${body.error}` : "";
    } catch {
      // A non-JSON error body; the status code alone is still useful.
    }
    throw new Error(`Permintaan gagal (HTTP ${res.status})${detail}`);
  }
  return res.json();
}

export function useApi<T>(load: () => Promise<T>): ApiState<T> & { reload: () => void } {
  const [state, setState] = useState<ApiState<T>>({ status: "loading", data: null, error: null });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let live = true;
    setState({ status: "loading", data: null, error: null });
    loadRef.current().then(
      (data) => live && setState({ status: "ready", data, error: null }),
      (err: unknown) =>
        live &&
        setState({
          status: "error",
          data: null,
          error: err instanceof Error ? err.message : "Tidak dapat menghubungi server.",
        }),
    );
    return () => {
      live = false;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
